import { useEffect } from "react";
import { LogOut } from "lucide-react";
import { track } from "./lib/analytics";
import { btnPrimary } from "./admin-ui";
import { Doodle, Sticker, WindowBar } from "./design-system";
import { Photo } from "./photo";

type GateCourse = {
  slug: string;
  title: string;
  subtitle?: string;
  description: string;
  thumbnailUrl?: string | null;
  basePriceMinor?: number;
  baseCurrency?: string;
  modules: Array<{ lessons: unknown[] }>;
};

const copy = {
  kicker: "Tu espacio en hey404",
  title: "Empieza tu primer curso",
  body: "Cómpralo y aquí mismo vas a ver tus clases, tu progreso y tu certificado.",
  lessons: (modules: number, lessons: number) =>
    `${modules} módulos · ${lessons} clases`,
  buy: "Comprar el curso",
  buyFor: (price: string) => `Comprar por ${price}`,
  founderBar: "Por qué existe hey404",
  founderTitle: "Buenas ideas, sin experiencia técnica",
  founderBody:
    "Esteban, el fundador, cuenta por qué creamos hey404 y qué vas a construir.",
  videoLabel: "Esteban cuenta por qué existe hey404",
  videoFallback: "Tu navegador no admite la reproducción de este video.",
  otherEmail: "¿Lo compraste con otro correo? Cierra sesión y entra con ese.",
  help: "¿Dudas?",
  helpLink: "Escríbenos",
  signOut: "Cerrar sesión",
};

// The landing's founder video (apps/landing, components/approved/Founder.astro),
// served from hey404.com.
const founderVideo =
  "https://hey404.com/approved-v30/media/founder-course-introduction.mp4";
const founderPoster =
  "https://hey404.com/approved-v30/images/founder-poster.jpg";

const formatPrice = (amountMinor: number, currency: string) =>
  new Intl.NumberFormat("es", {
    style: "currency",
    currency,
    minimumFractionDigits: amountMinor % 100 === 0 ? 0 : 2,
  }).format(amountMinor / 100);

// Both cards share this frame so the two halves mirror each other.
const card =
  "border-ink bg-bone border-surface shadow-brutal-lg flex min-w-0 flex-col overflow-hidden";

/**
 * What a signed-in student without any course sees instead of the platform:
 * the course on sale beside the founder's video, both on one screen, and a
 * single way forward — buying it. The checkout knows them already, so they
 * pay as themselves.
 */
export function BuyCourseGate({
  course,
  onSignOut,
}: {
  course: GateCourse | undefined;
  onSignOut: () => void;
}) {
  useEffect(() => {
    track("buy_gate_viewed", { course: course?.slug });
  }, [course?.slug]);
  const lessonCount =
    course?.modules.reduce((sum, module) => sum + module.lessons.length, 0) ??
    0;
  const price =
    course?.basePriceMinor && course.baseCurrency
      ? formatPrice(course.basePriceMinor, course.baseCurrency)
      : null;
  return (
    <div className="bg-paper text-ink platform-grid flex min-h-dvh flex-col">
      <header className="border-ink bg-paper border-b-(length:--border-surface) flex h-16 shrink-0 items-center px-4 sm:px-5">
        <img src="/logo-final.svg" alt="hey404" className="h-10 w-auto" />
        <button
          type="button"
          onClick={onSignOut}
          className="border-ink bg-bone shadow-brutal border-chip ml-auto cursor-pointer p-2"
          aria-label={copy.signOut}
          title={copy.signOut}
        >
          <LogOut size={17} />
        </button>
      </header>
      <main className="mx-auto flex w-full max-w-5xl flex-1 flex-col justify-center px-5 py-6 sm:px-8">
        <div className="text-center">
          <Sticker tone="lemon" tilt={-2} className="gate-short-hide">
            {copy.kicker}
          </Sticker>
          <h1 className="gate-title font-display mt-3 text-4xl font-extrabold sm:text-5xl">
            {copy.title}
          </h1>
          <p className="gate-short-hide text-ink-soft mx-auto mt-2 max-w-2xl text-lg">
            {copy.body}
          </p>
        </div>

        {/* On wide screens the pair shrinks with the window's height (keeping
            16:9 media), so the whole screen fits without scrolling. */}
        <div className="gate-pair relative mx-auto mt-7 grid w-full items-stretch gap-6 md:grid-cols-2 md:gap-8">
          <Doodle
            name="flower-yellow"
            float
            tilt={10}
            className="absolute -right-5 -top-8 z-10 w-14"
          />
          {course && (
            <article className={card}>
              <WindowBar tone="grape" title={course.title} />
              {course.thumbnailUrl && (
                <Photo
                  src={course.thumbnailUrl}
                  alt=""
                  className="border-ink border-b-(length:--border-surface) aspect-video h-auto w-full object-cover"
                />
              )}
              <div className="flex flex-1 flex-col p-4 sm:p-5">
                <h2 className="font-display text-2xl font-extrabold">
                  {course.title}
                </h2>
                <p className="text-ink-soft mb-5 mt-1.5 font-mono text-xs font-bold uppercase tracking-[0.06em]">
                  {lessonCount > 0
                    ? copy.lessons(course.modules.length, lessonCount)
                    : course.subtitle}
                </p>
                <a
                  href={`/checkout?courses=${encodeURIComponent(course.slug)}`}
                  className={`${btnPrimary} mt-auto w-full justify-between whitespace-nowrap px-5 py-3 text-base`}
                >
                  {price ? copy.buyFor(price) : copy.buy}{" "}
                  <span aria-hidden="true">↗</span>
                </a>
              </div>
            </article>
          )}
          <article className={card}>
            <WindowBar tone="lemon" title={copy.founderBar} />
            <video
              controls
              playsInline
              preload="none"
              poster={founderPoster}
              aria-label={copy.videoLabel}
              className="bg-ink border-ink border-b-(length:--border-surface) block aspect-video w-full"
            >
              <source src={founderVideo} type="video/mp4" />
              {copy.videoFallback}
            </video>
            <div className="flex flex-1 flex-col p-4 sm:p-5">
              <h2 className="font-display text-2xl font-extrabold">
                {copy.founderTitle}
              </h2>
              <p className="text-ink-soft mt-1.5">{copy.founderBody}</p>
            </div>
          </article>
        </div>

        <p className="text-ink-soft mt-5 text-center text-sm">
          {copy.otherEmail} {copy.help}{" "}
          <a
            href="mailto:hey404lab@gmail.com"
            className="text-ink font-bold underline"
          >
            {copy.helpLink}
          </a>
        </p>
      </main>
    </div>
  );
}
